/**
 * Cuadro de detalle de un indicador (estilo Shopify "Ventas totales a lo largo del tiempo").
 * 100% Polaris-first: Text + Box; el gráfico es polaris-viz LineChart.
 * Se renderiza INTEGRADO dentro del cuadro del KpiBar v2 (slot `detail`), como una fila
 * inferior bajo la fila de indicadores → un solo contenedor, igual que Shopify.
 *
 * Incluye la línea de comparación del periodo anterior (punteada, `isComparison`),
 * estilo Shopify, cuando se pasa `comparisonSeries`.
 */
import { BlockStack, InlineStack, Text } from '@/lib/polaris';
import { LineChart, PolarisVizProvider } from '@shopify/polaris-viz';
import type { DataSeries } from '@shopify/polaris-viz';
import '@shopify/polaris-viz/build/esm/styles.css';

export interface KpiDetailChartProps {
  /** Nombre del indicador (p. ej. "Ventas"). */
  label: string;
  /** Valor formateado (p. ej. "S/ 9,575.00"). */
  value: string;
  /** Serie del periodo actual. */
  series: number[];
  /** Etiquetas del eje X (misma longitud que `series`). */
  labels: string[];
  /** Nodo opcional con el delta (flecha + %) para el encabezado. */
  deltaNode?: React.ReactNode;
  /** Serie del periodo anterior (misma longitud) → línea punteada de comparación. */
  comparisonSeries?: number[];
}

export function KpiDetailChart({ label, value, series, labels, deltaNode, comparisonSeries }: KpiDetailChartProps) {
  const data: DataSeries[] = [
    {
      name: label,
      data: series.map((v, i) => ({ key: labels[i] ?? String(i), value: v })),
    },
    ...(comparisonSeries && comparisonSeries.length > 0
      ? [{
          name: 'Periodo anterior',
          isComparison: true,
          styleOverride: { line: { hasArea: false, strokeDasharray: '3 4' } },
          data: comparisonSeries.map((v, i) => ({ key: labels[i] ?? String(i), value: v })),
        } as DataSeries]
      : []),
  ];
  return (
    <BlockStack gap="300">
      <BlockStack gap="100">
        <Text as="h3" variant="bodySm" tone="subdued" fontWeight="medium">
          {`${label} a lo largo del tiempo`}
        </Text>
        <InlineStack gap="200" blockAlign="center" wrap={false}>
          <Text as="p" variant="headingXl" fontWeight="semibold">{value}</Text>
          {deltaNode}
        </InlineStack>
      </BlockStack>
      <PolarisVizProvider>
        {/* Alto FIJO (no minHeight): polaris-viz llena exactamente el contenedor, así no
            queda espacio blanco abajo del cuadro. ~240px = compacto estilo Shopify. */}
        <div style={{ height: 240 }}>
          <LineChart
            data={data}
            theme="Default"
            tooltipOptions={{ titleFormatter: (title) => String(title) }}
          />
        </div>
      </PolarisVizProvider>
    </BlockStack>
  );
}
